import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"
import type { BadgeCheck } from "lucide-react"
import {
  ArrowRight,
  CalendarClock,
  CircleCheckBig,
  CreditCard,
  RefreshCw,
  ReceiptText,
  TriangleAlert,
} from "lucide-react"
import { useState } from "react"

import { DemoNotice } from "@/components/demo-notice"
import { PublicPagePending } from "@/components/loading-skeleton"
import { PublicHeader } from "@/components/public-header"
import { Button } from "@/components/ui/button"
import { formatCurrency, formatTimestamp } from "@/lib/date"
import {
  golfDeskQueryKeys,
  membershipQueryOptions,
  viewerQueryOptions,
} from "@/lib/query-options"
import {
  abandonMembershipCheckoutFn,
  startMembershipCheckoutFn,
  syncMembershipCheckoutFn,
} from "@/server/functions"

type MembershipSearch = {
  checkout?: "success" | "cancelled"
}

export const Route = createFileRoute("/membership")({
  validateSearch: (search: Record<string, unknown>): MembershipSearch => ({
    checkout:
      search.checkout === "success" || search.checkout === "cancelled"
        ? search.checkout
        : undefined,
  }),
  loaderDeps: ({ search }) => search,
  loader: async ({ context, deps }) => {
    const viewer =
      await context.queryClient.ensureQueryData(viewerQueryOptions())

    if (!viewer || viewer.role !== "golfer") {
      throw redirect({ to: "/login", search: { next: "/membership" } })
    }

    if (deps.checkout === "success") {
      await syncMembershipCheckoutFn()
    } else if (deps.checkout === "cancelled") {
      await abandonMembershipCheckoutFn()
    }

    await context.queryClient.invalidateQueries({
      queryKey: golfDeskQueryKeys.membership(),
    })
    return context.queryClient.fetchQuery(membershipQueryOptions())
  },
  head: () => ({
    meta: [{ title: "Player Pass · Groundline" }],
  }),
  pendingComponent: PublicPagePending,
  component: Membership,
})

function Membership() {
  const { data } = useSuspenseQuery(membershipQueryOptions())
  const search = Route.useSearch()
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState("")
  const subscription = data.subscription
  const isSubscribed =
    subscription &&
    ["active", "trialing", "past_due"].includes(subscription.status)

  async function startCheckout() {
    setStarting(true)
    setError("")

    try {
      const result = await startMembershipCheckoutFn()

      if (result.ok) {
        window.location.assign(result.checkoutUrl)
        return
      }

      setError(
        result.reason === "ALREADY_SUBSCRIBED"
          ? "This demo account already has an active Player Pass."
          : "Stripe test checkout is temporarily unavailable. Please try again."
      )
    } catch {
      setError("The subscription checkout could not be started.")
    } finally {
      setStarting(false)
    }
  }

  return (
    <>
      <PublicHeader viewer={data.viewer} />
      <DemoNotice />
      <main className="site-frame py-10 sm:py-14">
        <div className="grid gap-7 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
          <div>
            <span className="eyebrow">
              <RefreshCw className="size-4" />
              Recurring billing
            </span>
            <h1 className="page-title">The Player Pass.</h1>
            <p className="page-lede">
              A complete Stripe subscription proof: hosted checkout, signed
              lifecycle webhooks, account-linked status, and invoice history.
            </p>

            {search.checkout === "success" && isSubscribed ? (
              <div className="mt-6 flex items-start gap-3 rounded-[10px] border border-[#9fbeb8] bg-[#dcebe7] p-4 text-sm">
                <CircleCheckBig className="mt-0.5 size-5 shrink-0 text-primary" />
                <div>
                  <strong className="font-semibold">Player Pass active</strong>
                  <p className="mt-1 leading-6">
                    Stripe’s Checkout Session and subscription are now linked to
                    this golfer account.
                  </p>
                </div>
              </div>
            ) : search.checkout === "cancelled" ? (
              <div className="mt-6 flex items-start gap-3 rounded-[10px] border border-[#d1aa73] bg-[#f4ead8] p-4 text-sm">
                <TriangleAlert className="mt-0.5 size-5 shrink-0 text-[#8a5523]" />
                <div>
                  <strong className="font-semibold">
                    Subscription checkout cancelled
                  </strong>
                  <p className="mt-1 leading-6">
                    No subscription was started and no payment was taken.
                  </p>
                </div>
              </div>
            ) : null}

            <section className="ruled-panel mt-8 overflow-hidden">
              <div className="ruled-panel__head">
                <div>
                  <span className="eyebrow">
                    <ReceiptText className="size-3.5" />
                    Billing record
                  </span>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Synced from Stripe invoice events
                  </p>
                </div>
              </div>
              {data.payments.length === 0 ? (
                <div className="empty-state">
                  <div>
                    <ReceiptText className="size-6" />
                    <strong>No subscription invoices yet</strong>
                    <p className="mt-2">
                      The first successful test invoice will appear here.
                    </p>
                  </div>
                </div>
              ) : (
                data.payments.map((payment) => (
                  <div
                    key={payment.id}
                    className="rule-row grid gap-3 px-5 py-4 sm:grid-cols-[1fr_auto] sm:items-center"
                  >
                    <div>
                      <strong className="block text-sm">
                        {payment.description}
                      </strong>
                      <small className="mt-1 block text-xs text-muted-foreground">
                        {formatTimestamp(payment.paidAt ?? payment.createdAt)}
                      </small>
                    </div>
                    <div className="sm:text-right">
                      <strong className="block">
                        {formatCurrency(payment.amountCents, payment.currency)}
                      </strong>
                      <small className="mt-1 block text-xs font-semibold text-muted-foreground capitalize">
                        {payment.status}
                      </small>
                    </div>
                  </div>
                ))
              )}
            </section>
          </div>

          <aside className="ruled-panel overflow-hidden lg:sticky lg:top-[94px]">
            <div className="bg-primary p-5 text-white">
              <span className="text-[0.65rem] font-bold tracking-[0.13em] text-[#b8ccc6] uppercase">
                Player Pass
              </span>
              <strong className="mt-2 block font-display text-[2.4rem] leading-none font-semibold uppercase">
                {formatCurrency(data.plan.amountCents, data.plan.currency)}
              </strong>
              <span className="mt-2 block text-xs text-[#c4d4cf]">
                per month · Stripe test mode
              </span>
            </div>
            <div className="grid gap-4 p-5">
              <PassRow
                icon={CreditCard}
                title="Hosted checkout"
                copy="Card details stay on Stripe"
              />
              <PassRow
                icon={RefreshCw}
                title="Recurring lifecycle"
                copy="Updates and cancellations sync by webhook"
              />
              <PassRow
                icon={CalendarClock}
                title="Period tracking"
                copy={
                  subscription?.currentPeriodEnd
                    ? `Current period ends ${formatTimestamp(
                        subscription.currentPeriodEnd
                      )}`
                    : "Starts after successful checkout"
                }
              />

              <div className="border-t border-dashed border-border pt-4">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs font-semibold text-muted-foreground">
                    Account status
                  </span>
                  <span
                    className={`status-chip ${
                      isSubscribed ? "status-chip--confirmed" : ""
                    }`}
                  >
                    {subscription?.status.replaceAll("_", " ") ?? "Not joined"}
                  </span>
                </div>
              </div>

              {error ? (
                <p className="action-error" role="alert">
                  {error}
                </p>
              ) : null}

              <Button
                variant="accent"
                size="lg"
                className="w-full"
                disabled={
                  starting || Boolean(isSubscribed) || !data.paymentsReady
                }
                onClick={startCheckout}
              >
                {starting
                  ? "Opening Stripe checkout…"
                  : isSubscribed
                    ? "Player Pass active"
                    : "Start test subscription"}
                {!isSubscribed ? <ArrowRight data-icon="inline-end" /> : null}
              </Button>
              {!data.paymentsReady ? (
                <p className="text-center text-xs leading-5 text-muted-foreground">
                  The test subscription price has not been configured.
                </p>
              ) : (
                <p className="text-center text-[0.69rem] leading-5 text-muted-foreground">
                  Use Stripe test card 4242 4242 4242 4242.
                </p>
              )}
            </div>
          </aside>
        </div>
      </main>
    </>
  )
}

function PassRow({
  icon: Icon,
  title,
  copy,
}: {
  icon: typeof BadgeCheck
  title: string
  copy: string
}) {
  return (
    <div className="flex gap-3 text-sm">
      <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
      <span>
        <strong className="block">{title}</strong>
        <small className="mt-1 block leading-5 text-muted-foreground">
          {copy}
        </small>
      </span>
    </div>
  )
}
